/**
 * v0.4 追加要件⑪ 章② (D-392): 編成ボードの表示絞り込みが依存する行単位の集計・判定。
 * `ShiftBoardPage.tsx` の useMemo に留めず抽出する (test-runner指摘: `pages/__tests__/ImportsPage.test.ts`
 * の `deriveCommitOutcome` と同じ理由 — ページ内ロジックのまま放置すると変更が無テストで壊れる)。
 */
import { isoToLocalHm, personKey, type PersonRef } from './shift-format';
import { resolveTimeBand, type TimeBand } from './time-band';

export type BoardFilterState = {
  confirmedOnly: boolean;
  unsubmittedOnly: boolean;
  violationsOnly: boolean;
  bandCode: string | null;
};

export const EMPTY_BOARD_FILTER: BoardFilterState = {
  confirmedOnly: false, unsubmittedOnly: false, violationsOnly: false, bandCode: null,
};

export type RowStat = {
  totalMinutes: number;
  hasConfirmed: boolean;
  hasPreference: boolean;
  hasViolation: boolean;
  bandCodes: Set<string>;
};

type AssignmentLike = PersonRef & { start_at: string; planned_labor_minutes: number; status: string };
type ViolationLike = { subject_employee_id: string | null; subject_external_person_id: string | null; acknowledged_at: string | null };

/** 行 (従業員/外部人材) ごとに合計勤務時間・確定済み有無・希望提出有無・未対応違反有無・
 *  出現する時間帯区分コードを集計する。`rowKeys` に無いキーの assignment/preference/violation は無視する
 *  (ロースターに現れない孤立データ、通常発生しない想定)。 */
export const buildRowStats = (
  rowKeys: readonly string[],
  assignments: readonly AssignmentLike[],
  preferences: readonly PersonRef[],
  violations: readonly ViolationLike[],
  timeBands: readonly TimeBand[],
): Map<string, RowStat> => {
  const map = new Map<string, RowStat>();
  for (const key of rowKeys) map.set(key, { totalMinutes: 0, hasConfirmed: false, hasPreference: false, hasViolation: false, bandCodes: new Set() });
  const bands = [...timeBands];

  for (const a of assignments) {
    const stat = map.get(personKey(a));
    if (!stat) continue;
    stat.totalMinutes += a.planned_labor_minutes;
    if (a.status === 'confirmed') stat.hasConfirmed = true;
    const band = resolveTimeBand(isoToLocalHm(a.start_at), bands);
    if (band) stat.bandCodes.add(band.code);
  }
  for (const p of preferences) {
    const stat = map.get(personKey(p));
    if (stat) stat.hasPreference = true;
  }
  for (const v of violations) {
    // 対応済みの違反は「違反のある行のみ」の対象外 (未対応のみが実際の懸念)
    if (v.acknowledged_at) continue;
    if (!v.subject_employee_id && !v.subject_external_person_id) continue;
    const stat = map.get(personKey({ employee_id: v.subject_employee_id, external_person_id: v.subject_external_person_id }));
    if (stat) stat.hasViolation = true;
  }
  return map;
};

/** 行が現在のフィルタ条件に合致するか。stat が無い行 (ロースター上のみで割当実績が無い等) は
 *  条件が1つでも有効なら除外する (§8-89型の「取りこぼし」を防ぐため fail-closed)。 */
export const matchesBoardFilter = (stat: RowStat | undefined, filter: BoardFilterState): boolean => {
  if (!stat) return !isBoardFilterActive(filter);
  if (filter.confirmedOnly && !stat.hasConfirmed) return false;
  if (filter.unsubmittedOnly && stat.hasPreference) return false;
  if (filter.violationsOnly && !stat.hasViolation) return false;
  if (filter.bandCode && !stat.bandCodes.has(filter.bandCode)) return false;
  return true;
};

export const isBoardFilterActive = (filter: BoardFilterState): boolean =>
  filter.confirmedOnly || filter.unsubmittedOnly || filter.violationsOnly || !!filter.bandCode;
